import { useEffect, useState } from "react";
import { Globe, Handshake, Layers } from "lucide-react";
import { adminApi } from "../../lib/api";

const SEGMENTS = [
  { id: "all", label: "All traffic", icon: Layers },
  { id: "organic", label: "Organic", icon: Globe },
  { id: "affiliate", label: "Affiliate", icon: Handshake },
];

export function StatsTab() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [stale, setStale] = useState(null);
  const [segment, setSegment] = useState("all");
  useEffect(() => {
    adminApi().get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    adminApi().get("/admin/health/booking").then((r) => setHealth(r.data)).catch(() => {});
    adminApi().get("/admin/routes/stale").then((r) => setStale(r.data)).catch(() => {});
  }, []);
  if (!stats) return <p className="text-slate-400 text-sm">Loading...</p>;

  const seg = segment === "all" ? stats.events : (stats.events_split?.[segment] || {});
  const trafficCards = [
    { l: "Searches Started", v: seg?.search_initiated || 0 },
    { l: "Booking Handoffs", v: seg?.booking_handoff || 0 },
    { l: "WhatsApp Clicks", v: seg?.whatsapp_click || 0 },
    { l: "Page Views (campaign)", v: seg?.page_view || 0 },
  ];
  const attribution = stats.attribution || {};
  const healthColor = health?.status === "green" ? "bg-green-100 text-green-800 border-green-200"
                    : health?.status === "yellow" ? "bg-amber-100 text-amber-800 border-amber-200"
                    : health?.status === "red" ? "bg-red-100 text-red-800 border-red-200"
                    : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <div>
      {(health || (stale && stale.count > 0)) && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="admin-health-row">
          {health && (
            <div className={`border rounded-xl p-4 ${healthColor}`} data-testid="admin-booking-health-card">
              <p className="text-xs uppercase tracking-wide font-bold mb-1">Booking funnel · last 2h</p>
              <p className="font-heading text-2xl font-bold">
                {health.status === "green" && "Healthy"}
                {health.status === "yellow" && "Below threshold"}
                {health.status === "red" && "Broken"}
                {health.status === "unknown" && "Low traffic"}
              </p>
              <p className="text-xs mt-1 opacity-90">{health.message}</p>
            </div>
          )}
          {stale && stale.count > 0 && (
            <div className="border rounded-xl p-4 bg-amber-50 border-amber-200 text-amber-900" data-testid="admin-stale-routes-card">
              <p className="text-xs uppercase tracking-wide font-bold mb-1">Stale route prices</p>
              <p className="font-heading text-2xl font-bold">{stale.count} route{stale.count !== 1 ? "s" : ""}</p>
              <p className="text-xs mt-1 opacity-90">
                Prices not verified in {stale.threshold_days}+ days. Open the Routes tab to re-verify.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6" data-testid="admin-leads-cards">
        <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow duration-200">
          <p className="font-heading text-3xl font-bold text-slate-900">{stats.leads_total}</p>
          <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Total Leads</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow duration-200">
          <p className="font-heading text-3xl font-bold text-slate-900">{stats.leads_new}</p>
          <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">New Leads</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">Traffic breakdown</p>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1" data-testid="admin-traffic-segment-toggle">
          {SEGMENTS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-segment-${s.id}`}
              onClick={() => setSegment(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors duration-200 ${
                segment === s.id ? "bg-[#970C10] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats-cards">
        {trafficCards.map((c) => (
          <div key={c.l} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow duration-200">
            <p className="font-heading text-3xl font-bold text-slate-900">{c.v}</p>
            <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">{c.l}</p>
            {segment !== "all" && (
              <p className={`text-[10px] font-bold uppercase tracking-wide mt-1 ${segment === "affiliate" ? "text-[#970C10]" : "text-slate-400"}`}>
                {segment === "affiliate" ? "Affiliate-driven" : "Organic only"}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-lead-sources">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Top lead sources (utm_source)</p>
          {(attribution.top_lead_sources || []).length === 0 && (
            <p className="text-xs text-slate-400">{"No attributed leads yet — link URLs like "}<code className="bg-slate-100 px-1 rounded">?utm_source=fb&amp;utm_campaign=umrah</code></p>
          )}
          <div className="space-y-2">
            {(attribution.top_lead_sources || []).map((s) => (
              <div key={s.source} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-slate-700 truncate">{s.source}</span>
                <span className="ml-auto font-bold text-[#970C10]">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-event-sources">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Top event sources (utm_source)</p>
          {(attribution.top_event_sources || []).length === 0 && (
            <p className="text-xs text-slate-400">No attributed events yet.</p>
          )}
          <div className="space-y-2">
            {(attribution.top_event_sources || []).map((s) => (
              <div key={s.source} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-slate-700 truncate">{s.source}</span>
                <span className="ml-auto font-bold text-[#970C10]">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Recent events</p>
        <div className="space-y-2">
          {stats.recent_events.map((e) => (
            <div key={e.id} className="flex items-center gap-3 text-xs text-slate-600 border-b border-slate-100 pb-2 last:border-0">
              <span className="font-bold text-[#970C10] w-32 shrink-0">{e.event_type}</span>
              <span className="font-mono truncate">{e.page}</span>
              {e.attribution?.aff && (
                <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide bg-red-50 text-[#970C10] border border-red-100 px-1.5 py-0.5 rounded-full" data-testid={`event-aff-badge-${e.id}`}>
                  aff: {e.attribution.aff}
                </span>
              )}
              <span className="ml-auto shrink-0 text-slate-400">{new Date(e.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
